// Invoice fixtures for the FE-only demo. All Bulgarian, amounts in EUR.
// The hero invoices (1-8) carry field-for-field data matching the PDFs in
// public/demo/invoices/. Shapes mirror the SaldoReview consumers exactly.

import { demoClients } from './clients';

export interface ExtractedField {
  id: string;
  field_name: string;
  field_value: string;
  confidence_score: number;
  is_validated: boolean;
  is_corrected: boolean;
  original_value: string | null;
  page_number: number;
  line_item_group?: string;
  extra_metadata?: Record<string, unknown> | null;
}

export interface ReprLineItem {
  line_item_group: string;
  quantity: string | null;
  unit_price: string | null;
  line_total: string | null;
  line_tax: string | null;
  vat_rate: string | null;
  discount_percent: string | null;
  discount_amount: string | null;
}

export interface CurrencyRepresentation {
  id: string;
  invoice_id: string;
  currency_code: string;
  source: 'extracted' | 'calculated';
  is_primary: boolean;
  subtotal: string | null;
  tax_amount: string | null;
  total_amount: string;
  line_items: ReprLineItem[] | null;
  exchange_rate: string | null;
  rate_date: string | null;
  calculated_from_currency: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnalyticPart {
  acc: string;
  level1?: string;
  level1_name?: string;
  level2?: string;
  level2_name?: string;
}

export interface ErpLineItem {
  line_item_group: string;
  deal_name?: number;
  acc_assignment?: number;
  analytic_parts?: AnalyticPart[];
  without_account_assignment?: boolean;
}

export interface DemoInvoice {
  id: string;
  invoice_number: string;
  mirage_invoice_number: string | null;
  invoice_date: string; // ISO
  uploaded_at: string;
  processed_at: string;
  status: string;
  invoice_type: 'purchase' | 'sales';
  overall_confidence: number;
  original_filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  error_message?: string;
  pdfUrl: string;
  clientId: string;
  mirage_counterparty_id: number | null;
  active_currency_code: string;
  extractedData: ExtractedField[];
  representation: CurrencyRepresentation;
  additional_metadata: Record<string, unknown>;
  currency_rate_info: Record<string, unknown>;
  _processingUntil?: number;
}

const PDF_BASE = '/demo/invoices/';

const FIELD_OPTIONS = {
  message_type: [
    { value: 'Фактура', label: 'Фактура' },
    { value: 'Кредитно известие', label: 'Кредитно известие' },
    { value: 'Дебитно известие', label: 'Дебитно известие' },
  ],
  document_subject: [
    { value: 'Получена услуга', label: 'Получена услуга' },
    { value: 'Закупени стоки', label: 'Закупени стоки' },
    { value: 'Актив (ДМА)', label: 'Актив (ДМА)' },
    { value: 'Получена услуга (ВОП)', label: 'Получена услуга (ВОП)' },
  ],
};

interface LineInput {
  group: string;
  description: string;
  unit?: string;
  qty?: string;
  unitPrice: string;
  lineTotal: string;
  vat?: string;
  lineTax?: string;
  erp?: Omit<ErpLineItem, 'line_item_group'>;
}

interface Party {
  name: string;
  address: string;
  taxId: string;
  country: string;
  email?: string;
  phone?: string;
}

interface InvoiceInput {
  id: string;
  invoiceNumber: string;
  mirageNumber?: string | null;
  date: string;
  uploadedAt?: string;
  clientId: string;
  status: string;
  pdf: string;
  /** Display filename (defaults to the pdf asset name). */
  filename?: string;
  supplier: Party;
  subtotal: string;
  tax: string;
  total: string;
  lines: LineInput[];
  invoiceType?: 'purchase' | 'sales';
  mirageCounterpartyId?: number | null;
  paymentTerms?: string;
  additionalRemarks?: string;
  messageType?: string;
  notes?: string;
  confidence?: number;
  errorMessage?: string;
}

function f(
  invId: string,
  name: string,
  value: string,
  extra?: Partial<ExtractedField>
): ExtractedField {
  return {
    id: `${invId}::${name}`,
    field_name: name,
    field_value: value,
    confidence_score: extra?.confidence_score ?? 0.95,
    is_validated: false,
    is_corrected: false,
    original_value: null,
    page_number: 1,
    ...extra,
  };
}

function buildInvoice(input: InvoiceInput): DemoInvoice {
  const client = demoClients.find((c) => c.id === input.clientId) ?? demoClients[0];
  const uploadedAt = input.uploadedAt ?? `${input.date}T08:12:00Z`;
  const processedAt = `${input.date}T08:14:30Z`;
  const invoiceType = input.invoiceType ?? 'purchase';

  const fields: ExtractedField[] = [];

  // Invoice-level
  fields.push(f(input.id, 'invoice_number', input.invoiceNumber));
  if (input.mirageNumber !== undefined && input.mirageNumber !== null) {
    fields.push(f(input.id, 'mirage_invoice_number', input.mirageNumber));
  }
  fields.push(f(input.id, 'invoice_date', input.date));
  fields.push(f(input.id, 'payment_terms', input.paymentTerms ?? 'По банков път'));
  fields.push(f(input.id, 'message_type', input.messageType ?? 'Фактура'));
  fields.push(f(input.id, 'additional_remarks', input.additionalRemarks ?? 'Получена услуга'));
  if (input.notes) fields.push(f(input.id, 'notes', input.notes));

  // Vendor (supplier)
  fields.push(f(input.id, 'vendor_name', input.supplier.name));
  fields.push(f(input.id, 'vendor_address', input.supplier.address));
  fields.push(f(input.id, 'vendor_tax_id', input.supplier.taxId));
  fields.push(f(input.id, 'vendor_country', input.supplier.country));
  if (input.supplier.email) fields.push(f(input.id, 'vendor_email', input.supplier.email));
  if (input.supplier.phone) fields.push(f(input.id, 'vendor_phone', input.supplier.phone));

  // Customer (the client is the buyer on purchase invoices)
  fields.push(f(input.id, 'customer_name', client.name));
  fields.push(f(input.id, 'customer_address', 'ул. Индустриална 12, София 1000'));
  fields.push(f(input.id, 'customer_tax_id', client.vat_code ?? ''));
  fields.push(f(input.id, 'customer_country', 'BG'));

  // Amounts (rendered in the Currency & Amounts section)
  fields.push(f(input.id, 'subtotal', input.subtotal));
  fields.push(f(input.id, 'tax_amount', input.tax));
  fields.push(f(input.id, 'total_amount', input.total));
  fields.push(f(input.id, 'currency', 'EUR'));

  // Line-item extracted fields (description + unit of measure per group)
  const reprLineItems: ReprLineItem[] = [];
  const erpLineItems: ErpLineItem[] = [];

  for (const line of input.lines) {
    fields.push(
      f(`${input.id}::li::${line.group}`, 'line_item_description', line.description, {
        line_item_group: line.group,
      })
    );
    if (line.unit) {
      fields.push(
        f(`${input.id}::li::${line.group}`, 'line_item_unit_of_measure', line.unit, {
          line_item_group: line.group,
        })
      );
    }

    reprLineItems.push({
      line_item_group: line.group,
      quantity: line.qty ?? '1',
      unit_price: line.unitPrice,
      line_total: line.lineTotal,
      line_tax: line.lineTax ?? null,
      vat_rate: line.vat ?? '20',
      discount_percent: null,
      discount_amount: null,
    });

    if (line.erp) {
      erpLineItems.push({
        line_item_group: line.group,
        deal_name: line.erp.deal_name,
        acc_assignment: line.erp.acc_assignment,
        analytic_parts: line.erp.analytic_parts ?? [],
        without_account_assignment: line.erp.without_account_assignment ?? false,
      });
    }
  }

  const representation: CurrencyRepresentation = {
    id: `${input.id}-repr-eur`,
    invoice_id: input.id,
    currency_code: 'EUR',
    source: 'extracted',
    is_primary: true,
    subtotal: input.subtotal,
    tax_amount: input.tax,
    total_amount: input.total,
    line_items: reprLineItems,
    exchange_rate: null,
    rate_date: input.date,
    calculated_from_currency: null,
    created_at: uploadedAt,
    updated_at: processedAt,
  };

  const currency_rate_info = {
    effective_rate: '1.00000000',
    shared_rate: '1.00000000',
    override_rate: null,
    source: 'фиксиран',
    rate_provider: 'ECB',
    rate_date: input.date,
    is_overridden: false,
  };

  const additional_metadata: Record<string, unknown> = {
    erp: {
      line_items: erpLineItems,
    },
    field_options: FIELD_OPTIONS,
  };

  const pdf = `${PDF_BASE}${input.pdf}`;

  return {
    id: input.id,
    invoice_number: input.invoiceNumber,
    mirage_invoice_number: input.mirageNumber ?? null,
    invoice_date: input.date,
    uploaded_at: uploadedAt,
    processed_at: processedAt,
    status: input.status,
    invoice_type: invoiceType,
    overall_confidence: input.confidence ?? 0.94,
    original_filename: input.filename ?? input.pdf,
    file_path: pdf,
    file_size: 58000,
    mime_type: 'application/pdf',
    error_message: input.errorMessage,
    pdfUrl: pdf,
    clientId: input.clientId,
    mirage_counterparty_id: input.mirageCounterpartyId ?? null,
    active_currency_code: 'EUR',
    extractedData: fields,
    representation,
    additional_metadata,
    currency_rate_info,
  };
}

// ── Counterparty pool (shared across invoices) ──────────────────────────────
export const counterpartyPool = [
  { id: 1001, name: 'Технополис ЕАД', name_lat: 'Technopolis EAD', code: '130858590', vat_code: 'BG130858590', address: 'бул. Цариградско шосе 361, София', city_name: 'София', country: 'BG', flags: { is_supplier: true, is_contragent: true, is_active: true } },
  { id: 1002, name: 'Спиди АД', name_lat: 'Speedy AD', code: '131371780', vat_code: 'BG131371780', address: 'ул. Самоковско шосе 2Л, София', city_name: 'София', country: 'BG', flags: { is_supplier: true, is_contragent: true, is_active: true } },
  { id: 1003, name: 'Интерлийз АД', name_lat: 'Interlease AD', code: '121840138', vat_code: 'BG121840138', address: 'бул. Тодор Александров 28, София', city_name: 'София', country: 'BG', flags: { is_supplier: true, is_creditor: true, is_active: true } },
  { id: 1004, name: 'ОМВ България ЕООД', name_lat: 'OMV Bulgaria', code: '130842217', vat_code: 'BG130842217', address: 'бул. Джавахарлал Неру 28, София', city_name: 'София', country: 'BG', flags: { is_supplier: true, is_contragent: true, is_active: true } },
  { id: 1005, name: 'Мега Строй ЕООД', name_lat: 'Mega Stroy EOOD', code: '201234567', vat_code: 'BG201234567', address: 'ул. Строителна 5, Пловдив', city_name: 'Пловдив', country: 'BG', flags: { is_supplier: true, is_active: true } },
  { id: 1006, name: 'Строй Инвест ООД', name_lat: 'Stroy Invest OOD', code: '203998112', vat_code: 'BG203998112', address: 'бул. България 102, София', city_name: 'София', country: 'BG', flags: { is_supplier: true, is_active: true } },
  { id: 1007, name: 'Adobe Systems Software Ireland Ltd', name_lat: 'Adobe Systems Software Ireland Ltd', code: '6364992H', vat_code: 'IE6364992H', address: '4-6 Riverwalk, Citywest, Dublin 24', city_name: 'Dublin', country: 'IE', flags: { is_supplier: true, is_active: true } },
];

// ── Fixtures ────────────────────────────────────────────────────────────────
export const demoInvoices: DemoInvoice[] = [
  // 1. Технополис — THE demo review invoice (ДМА split story)
  buildInvoice({
    id: 'inv-technopolis',
    invoiceNumber: '1000023841',
    mirageNumber: '1000023841',
    date: '2026-06-28',
    clientId: 'client-romil',
    status: 'review_required',
    pdf: 'inv-technopolis-1000023841.pdf',
    mirageCounterpartyId: 1001,
    supplier: { name: 'Технополис ЕАД', address: 'бул. Цариградско шосе 361, София 1000', taxId: 'BG130858590', country: 'BG', email: 'office@technopolis.bg', phone: '+359 2 4013 400' },
    subtotal: '2815.00',
    tax: '563.00',
    total: '3378.00',
    additionalRemarks: 'Актив (ДМА)',
    lines: [
      { group: '1', description: 'Лаптоп Dell Latitude 5550', unit: 'бр.', qty: '1', unitPrice: '2399.17', lineTotal: '2399.17', vat: '20', lineTax: '479.83', erp: { deal_name: 2, acc_assignment: 2, analytic_parts: [{ acc: '204', level1: '01', level1_name: 'Централен склад' }] } },
      { group: '2', description: 'Докинг станция Dell WD22TB4', unit: 'бр.', qty: '1', unitPrice: '165.83', lineTotal: '165.83', vat: '20', lineTax: '33.17', erp: { deal_name: 1, acc_assignment: 3, analytic_parts: [{ acc: '302', level1: '01', level1_name: 'Централен склад' }] } },
      { group: '3', description: 'Разширена гаранция 3г. ProSupport', unit: 'усл.', qty: '1', unitPrice: '250.00', lineTotal: '250.00', vat: '20', lineTax: '50.00', erp: { deal_name: 1, acc_assignment: 4, analytic_parts: [{ acc: '650', level1: '02', level1_name: 'Офис София' }] } },
    ],
  }),

  // 2. Спиди #2041 — duplicate-suspect
  buildInvoice({
    id: 'inv-spidi-2041',
    invoiceNumber: '0000002041',
    mirageNumber: '0000002041',
    date: '2026-07-03',
    clientId: 'client-romil',
    status: 'review_required',
    pdf: 'inv-spidi-2041.pdf',
    mirageCounterpartyId: 1002,
    supplier: { name: 'Спиди АД', address: 'ул. Самоковско шосе 2Л, София 1138', taxId: 'BG131371780', country: 'BG', email: 'info@speedy.bg' },
    subtotal: '3650.00',
    tax: '730.00',
    total: '4380.00',
    additionalRemarks: 'Получена услуга',
    lines: [
      { group: '1', description: 'Куриерски услуги — сборна фактура м.06', unit: 'усл.', qty: '1', unitPrice: '3650.00', lineTotal: '3650.00', vat: '20', lineTax: '730.00', erp: { deal_name: 1, acc_assignment: 1, analytic_parts: [{ acc: '602', level1: '02', level1_name: 'Офис София' }] } },
    ],
  }),

  // 3. Спиди #2018 — exported
  buildInvoice({
    id: 'inv-spidi-2018',
    invoiceNumber: '0000002018',
    mirageNumber: '0000002018',
    date: '2026-06-28',
    clientId: 'client-romil',
    status: 'exported',
    pdf: 'inv-spidi-2018.pdf',
    mirageCounterpartyId: 1002,
    supplier: { name: 'Спиди АД', address: 'ул. Самоковско шосе 2Л, София 1138', taxId: 'BG131371780', country: 'BG', email: 'info@speedy.bg' },
    subtotal: '3650.00',
    tax: '730.00',
    total: '4380.00',
    additionalRemarks: 'Получена услуга',
    lines: [
      { group: '1', description: 'Куриерски услуги — сборна фактура м.05', unit: 'усл.', qty: '1', unitPrice: '3650.00', lineTotal: '3650.00', vat: '20', lineTax: '730.00', erp: { deal_name: 1, acc_assignment: 1, analytic_parts: [{ acc: '602', level1: '02', level1_name: 'Офис София' }] } },
    ],
  }),

  // 4. Интерлийз — reviewed
  buildInvoice({
    id: 'inv-interlease',
    invoiceNumber: '0000091223',
    mirageNumber: '0000091223',
    date: '2026-07-01',
    clientId: 'client-romil',
    status: 'reviewed',
    pdf: 'inv-interlease-91223.pdf',
    mirageCounterpartyId: 1003,
    supplier: { name: 'Интерлийз АД', address: 'бул. Тодор Александров 28, София 1000', taxId: 'BG121840138', country: 'BG' },
    subtotal: '575.92',
    tax: '115.18',
    total: '691.10',
    additionalRemarks: 'Получена услуга',
    lines: [
      { group: '1', description: 'Лизингова вноска — автомобил CA1234XX', unit: 'усл.', qty: '1', unitPrice: '485.00', lineTotal: '485.00', vat: '20', lineTax: '97.00', erp: { deal_name: 1, acc_assignment: 1, analytic_parts: [{ acc: '602', level1: '02', level1_name: 'Офис София' }] } },
      { group: '2', description: 'Застраховка Каско', unit: 'усл.', qty: '1', unitPrice: '78.50', lineTotal: '78.50', vat: '20', lineTax: '15.70', erp: { deal_name: 1, acc_assignment: 6, analytic_parts: [{ acc: '609', level1: '02', level1_name: 'Офис София' }] } },
      { group: '3', description: 'Пътен данък', unit: 'усл.', qty: '1', unitPrice: '12.42', lineTotal: '12.42', vat: '20', lineTax: '2.48', erp: { deal_name: 1, acc_assignment: 6, analytic_parts: [{ acc: '609', level1: '02', level1_name: 'Офис София' }] } },
    ],
  }),

  // 5. Мега Строй — review_required
  buildInvoice({
    id: 'inv-megastroy',
    invoiceNumber: '0000000456',
    mirageNumber: '0000000456',
    date: '2026-07-02',
    clientId: 'client-romil',
    status: 'review_required',
    pdf: 'inv-megastroy-456.pdf',
    mirageCounterpartyId: 1005,
    supplier: { name: 'Мега Строй ЕООД', address: 'ул. Строителна 5, Пловдив 4000', taxId: 'BG201234567', country: 'BG' },
    subtotal: '7450.00',
    tax: '1490.00',
    total: '8940.00',
    additionalRemarks: 'Получена услуга',
    lines: [
      { group: '1', description: 'Строително-монтажни работи — обект Пловдив', unit: 'усл.', qty: '1', unitPrice: '7450.00', lineTotal: '7450.00', vat: '20', lineTax: '1490.00', erp: { deal_name: 1, acc_assignment: 1, analytic_parts: [{ acc: '602', level1: '03', level1_name: 'Обект Пловдив' }] } },
    ],
  }),

  // 6. ОМВ — reviewed (гориво = служебен разход)
  buildInvoice({
    id: 'inv-omv',
    invoiceNumber: '5501120843',
    mirageNumber: '5501120843',
    date: '2026-06-30',
    clientId: 'client-romil',
    status: 'reviewed',
    pdf: 'inv-omv-5501120843.pdf',
    mirageCounterpartyId: 1004,
    supplier: { name: 'ОМВ България ЕООД', address: 'бул. Джавахарлал Неру 28, София 1324', taxId: 'BG130842217', country: 'BG' },
    subtotal: '118.42',
    tax: '23.68',
    total: '142.10',
    additionalRemarks: 'Закупени стоки',
    lines: [
      { group: '1', description: 'Дизелово гориво Diesel', unit: 'л', qty: '92.45', unitPrice: '1.2809', lineTotal: '118.42', vat: '20', lineTax: '23.68', erp: { deal_name: 1, acc_assignment: 5, analytic_parts: [{ acc: '601', level1: '02', level1_name: 'Офис София' }] } },
    ],
  }),

  // 7. Строй Инвест — review_required (СМР минус аванс)
  buildInvoice({
    id: 'inv-stroyinvest',
    invoiceNumber: '0000000912',
    mirageNumber: '0000000912',
    date: '2026-07-04',
    clientId: 'client-romil',
    status: 'review_required',
    pdf: 'inv-stroyinvest-912.pdf',
    mirageCounterpartyId: 1006,
    supplier: { name: 'Строй Инвест ООД', address: 'бул. България 102, София 1680', taxId: 'BG203998112', country: 'BG' },
    subtotal: '7783.33',
    tax: '1556.67',
    total: '9340.00',
    additionalRemarks: 'Получена услуга',
    lines: [
      { group: '1', description: 'Строително-монтажни работи по договор №2026-11', unit: 'усл.', qty: '1', unitPrice: '18200.00', lineTotal: '18200.00', vat: '20', lineTax: '3640.00', erp: { deal_name: 1, acc_assignment: 1, analytic_parts: [{ acc: '602', level1: '01', level1_name: 'Централен склад' }] } },
      { group: '2', description: 'Приспаднат аванс по договор №2026-11', unit: 'усл.', qty: '1', unitPrice: '-10416.67', lineTotal: '-10416.67', vat: '20', lineTax: '-2083.33', erp: { deal_name: 1, acc_assignment: 1, analytic_parts: [{ acc: '602', level1: '01', level1_name: 'Централен склад' }] } },
    ],
  }),

  // 8. Adobe — Артемис, reverse charge (ВОП)
  buildInvoice({
    id: 'inv-adobe',
    invoiceNumber: 'IE2026-0704-1187',
    mirageNumber: 'IE2026-0704-1187',
    date: '2026-07-02',
    clientId: 'client-artemis',
    status: 'review_required',
    pdf: 'inv-adobe-artemis.pdf',
    mirageCounterpartyId: 1007,
    supplier: { name: 'Adobe Systems Software Ireland Ltd', address: '4-6 Riverwalk, Citywest Business Campus, Dublin 24', taxId: 'IE6364992H', country: 'IE', email: 'billing@adobe.com' },
    subtotal: '89.99',
    tax: '0.00',
    total: '89.99',
    additionalRemarks: 'Получена услуга (ВОП)',
    lines: [
      { group: '1', description: 'Adobe Creative Cloud — годишен абонамент (1 работно място)', unit: 'усл.', qty: '1', unitPrice: '89.99', lineTotal: '89.99', vat: '0', lineTax: '0.00', erp: { deal_name: 4, acc_assignment: 1, analytic_parts: [{ acc: '602', level1: '02', level1_name: 'Офис София' }] } },
    ],
  }),

  // ── Fillers (reuse hero PDFs) ─────────────────────────────────────────────
  buildInvoice({ id: 'inv-f-vivacom', filename: 'vivacom_7100045521.pdf', invoiceNumber: '7100045521', mirageNumber: '7100045521', date: '2026-06-05', clientId: 'client-romil', status: 'exported', pdf: 'inv-spidi-2041.pdf', mirageCounterpartyId: null, supplier: { name: 'Виваком България ЕАД', address: 'бул. Цариградско шосе 115, София', taxId: 'BG831642181', country: 'BG' }, subtotal: '84.52', tax: '16.90', total: '101.42', additionalRemarks: 'Получена услуга', lines: [{ group: '1', description: 'Месечни мобилни услуги м.05', unit: 'усл.', unitPrice: '84.52', lineTotal: '84.52', lineTax: '16.90', erp: { deal_name: 1, acc_assignment: 1 } }] }),
  buildInvoice({ id: 'inv-f-a1', filename: 'a1_faktura_8800112233.pdf', invoiceNumber: '8800112233', mirageNumber: '8800112233', date: '2026-06-06', clientId: 'client-romil', status: 'reviewed', pdf: 'inv-spidi-2018.pdf', supplier: { name: 'А1 България ЕАД', address: 'ул. Кукуш 1, София', taxId: 'BG131468980', country: 'BG' }, subtotal: '120.00', tax: '24.00', total: '144.00', additionalRemarks: 'Получена услуга', lines: [{ group: '1', description: 'Интернет и телефон — бизнес пакет', unit: 'усл.', unitPrice: '120.00', lineTotal: '120.00', lineTax: '24.00', erp: { deal_name: 1, acc_assignment: 1 } }] }),
  buildInvoice({ id: 'inv-f-electrohold', filename: 'electrohold_4400778812.pdf', invoiceNumber: '4400778812', mirageNumber: '4400778812', date: '2026-06-10', clientId: 'client-romil', status: 'exported', pdf: 'inv-omv-5501120843.pdf', supplier: { name: 'Електрохолд Продажби АД', address: 'бул. Цариградско шосе 159, София', taxId: 'BG175133827', country: 'BG' }, subtotal: '340.18', tax: '68.04', total: '408.22', additionalRemarks: 'Закупени стоки', lines: [{ group: '1', description: 'Електрическа енергия м.05', unit: 'усл.', unitPrice: '340.18', lineTotal: '340.18', lineTax: '68.04', erp: { deal_name: 1, acc_assignment: 1 } }] }),
  buildInvoice({ id: 'inv-f-sofiyskavoda', filename: 'sofiyska_voda_2200334455.pdf', invoiceNumber: '2200334455', mirageNumber: '2200334455', date: '2026-06-11', clientId: 'client-romil', status: 'reviewed', pdf: 'inv-megastroy-456.pdf', supplier: { name: 'Софийска вода АД', address: 'ул. Бизнес парк София 1, София', taxId: 'BG130175000', country: 'BG' }, subtotal: '45.60', tax: '9.12', total: '54.72', additionalRemarks: 'Закупени стоки', lines: [{ group: '1', description: 'Водоснабдяване и канализация м.05', unit: 'усл.', unitPrice: '45.60', lineTotal: '45.60', lineTax: '9.12', erp: { deal_name: 1, acc_assignment: 1 } }] }),
  buildInvoice({ id: 'inv-f-office1', filename: 'office1_9900556677.pdf', invoiceNumber: '9900556677', mirageNumber: '9900556677', date: '2026-06-14', clientId: 'client-romil', status: 'reviewed', pdf: 'inv-technopolis-1000023841.pdf', supplier: { name: 'Офис 1 ЕООД', address: 'бул. Христофор Колумб 41, София', taxId: 'BG831915840', country: 'BG' }, subtotal: '78.90', tax: '15.78', total: '94.68', additionalRemarks: 'Закупени стоки', lines: [{ group: '1', description: 'Офис консумативи и хартия', unit: 'бр.', qty: '12', unitPrice: '6.575', lineTotal: '78.90', lineTax: '15.78', erp: { deal_name: 1, acc_assignment: 3 } }] }),
  buildInvoice({ id: 'inv-f-lukoil', filename: 'lukoil_karti_6600223344.pdf', invoiceNumber: '6600223344', mirageNumber: '6600223344', date: '2026-06-17', clientId: 'client-romil', status: 'exported', pdf: 'inv-omv-5501120843.pdf', supplier: { name: 'Лукойл България ЕООД', address: 'бул. Тодор Александров 42, София', taxId: 'BG121699202', country: 'BG' }, subtotal: '210.00', tax: '42.00', total: '252.00', additionalRemarks: 'Закупени стоки', lines: [{ group: '1', description: 'Гориво по карти — м.05', unit: 'л', qty: '160', unitPrice: '1.3125', lineTotal: '210.00', lineTax: '42.00', erp: { deal_name: 1, acc_assignment: 5 } }] }),
  buildInvoice({ id: 'inv-f-metro', filename: 'metro_3300998877.pdf', invoiceNumber: '3300998877', mirageNumber: '3300998877', date: '2026-06-19', clientId: 'client-romil', status: 'reviewed', pdf: 'inv-megastroy-456.pdf', supplier: { name: 'Метро Кеш енд Кери България ЕООД', address: 'бул. Цариградско шосе 7-11 км, София', taxId: 'BG121644736', country: 'BG' }, subtotal: '512.34', tax: '102.47', total: '614.81', additionalRemarks: 'Закупени стоки', lines: [{ group: '1', description: 'Стоки за търговска дейност', unit: 'бр.', qty: '48', unitPrice: '10.6738', lineTotal: '512.34', lineTax: '102.47', erp: { deal_name: 1, acc_assignment: 3 } }] }),
  buildInvoice({ id: 'inv-f-kaufland', filename: 'kaufland_1200776655.pdf', invoiceNumber: '1200776655', mirageNumber: '1200776655', date: '2026-06-21', clientId: 'client-romil', status: 'completed', pdf: 'inv-technopolis-1000023841.pdf', supplier: { name: 'Кауфланд България ЕООД', address: 'ул. Скопие 1А, София', taxId: 'BG131129282', country: 'BG' }, subtotal: '96.20', tax: '19.24', total: '115.44', additionalRemarks: 'Закупени стоки', lines: [{ group: '1', description: 'Консумативи за офиса', unit: 'бр.', qty: '20', unitPrice: '4.81', lineTotal: '96.20', lineTax: '19.24', erp: { deal_name: 1, acc_assignment: 3 } }] }),
  buildInvoice({ id: 'inv-f-speedy3', filename: 'speedy_0000002099.pdf', invoiceNumber: '0000002099', mirageNumber: '0000002099', date: '2026-06-24', clientId: 'client-romil', status: 'exported', pdf: 'inv-spidi-2041.pdf', mirageCounterpartyId: 1002, supplier: { name: 'Спиди АД', address: 'ул. Самоковско шосе 2Л, София', taxId: 'BG131371780', country: 'BG' }, subtotal: '32.50', tax: '6.50', total: '39.00', additionalRemarks: 'Получена услуга', lines: [{ group: '1', description: 'Куриерски пратки', unit: 'усл.', unitPrice: '32.50', lineTotal: '32.50', lineTax: '6.50', erp: { deal_name: 1, acc_assignment: 1 } }] }),
  buildInvoice({ id: 'inv-f-yettel', filename: 'yettel_5500443322.pdf', invoiceNumber: '5500443322', mirageNumber: '5500443322', date: '2026-06-25', clientId: 'client-romil', status: 'reviewed', pdf: 'inv-spidi-2018.pdf', supplier: { name: 'Йеттел България ЕАД', address: 'ул. Демокрация 1, София', taxId: 'BG130460283', country: 'BG' }, subtotal: '64.00', tax: '12.80', total: '76.80', additionalRemarks: 'Получена услуга', lines: [{ group: '1', description: 'Мобилни услуги — допълнителни номера', unit: 'усл.', unitPrice: '64.00', lineTotal: '64.00', lineTax: '12.80', erp: { deal_name: 1, acc_assignment: 1 } }] }),
  buildInvoice({ id: 'inv-f-dzi', filename: 'dzi_polica_7700665544.pdf', invoiceNumber: '7700665544', mirageNumber: '7700665544', date: '2026-06-27', clientId: 'client-romil', status: 'reviewed', pdf: 'inv-interlease-91223.pdf', supplier: { name: 'ДЗИ Общо застраховане ЕАД', address: 'бул. Витоша 89Б, София', taxId: 'BG121718407', country: 'BG' }, subtotal: '150.00', tax: '0.00', total: '150.00', additionalRemarks: 'Получена услуга', lines: [{ group: '1', description: 'Застрахователна премия — имущество', unit: 'усл.', unitPrice: '150.00', lineTotal: '150.00', vat: '0', lineTax: '0.00', erp: { deal_name: 3, acc_assignment: 6 } }] }),
  buildInvoice({ id: 'inv-f-toplo', filename: 'toplofikacia_8100334411.pdf', invoiceNumber: '8100334411', mirageNumber: '8100334411', date: '2026-06-29', clientId: 'client-romil', status: 'exported', pdf: 'inv-omv-5501120843.pdf', supplier: { name: 'Топлофикация София ЕАД', address: 'ул. Ястребец 23Б, София', taxId: 'BG831609046', country: 'BG' }, subtotal: '88.75', tax: '17.75', total: '106.50', additionalRemarks: 'Закупени стоки', lines: [{ group: '1', description: 'Топлинна енергия м.05', unit: 'усл.', unitPrice: '88.75', lineTotal: '88.75', lineTax: '17.75', erp: { deal_name: 1, acc_assignment: 1 } }] }),
  buildInvoice({ id: 'inv-f-billa', filename: 'billa_2600889900.pdf', invoiceNumber: '2600889900', mirageNumber: '2600889900', date: '2026-07-01', clientId: 'client-romil', status: 'completed', pdf: 'inv-megastroy-456.pdf', supplier: { name: 'Билла България ЕООД', address: 'бул. Европа 168, София', taxId: 'BG130007884', country: 'BG' }, subtotal: '54.30', tax: '10.86', total: '65.16', additionalRemarks: 'Закупени стоки', lines: [{ group: '1', description: 'Стоки за представителни цели', unit: 'бр.', qty: '15', unitPrice: '3.62', lineTotal: '54.30', lineTax: '10.86', erp: { deal_name: 1, acc_assignment: 3 } }] }),
  buildInvoice({ id: 'inv-f-postbank', filename: 'postbank_taksi_3900112200.pdf', invoiceNumber: '3900112200', mirageNumber: '3900112200', date: '2026-07-03', clientId: 'client-romil', status: 'reviewed', pdf: 'inv-interlease-91223.pdf', supplier: { name: 'Юробанк България АД', address: 'ул. Околовръстен път 260, София', taxId: 'BG000694749', country: 'BG' }, subtotal: '25.00', tax: '0.00', total: '25.00', additionalRemarks: 'Получена услуга', lines: [{ group: '1', description: 'Банкови такси за обслужване', unit: 'усл.', unitPrice: '25.00', lineTotal: '25.00', vat: '0', lineTax: '0.00', erp: { deal_name: 3, acc_assignment: 6 } }] }),
];

// ── Upload simulation template (Виваком, monthly mobile invoice) ────────────
let uploadSeq = 0;
export function buildUploadInvoice(): DemoInvoice {
  uploadSeq += 1;
  const now = new Date();
  const id = `inv-upload-${Date.now()}-${uploadSeq}`;
  const inv = buildInvoice({
    id,
    invoiceNumber: '7100049988',
    mirageNumber: '7100049988',
    date: '2026-07-06',
    uploadedAt: now.toISOString(),
    clientId: 'client-romil',
    status: 'processing',
    pdf: 'inv-vivacom-7100049988.pdf',
    supplier: { name: 'Виваком България ЕАД', address: 'бул. Цариградско шосе 115, София', taxId: 'BG831642181', country: 'BG' },
    subtotal: '84.52',
    tax: '16.90',
    total: '101.42',
    additionalRemarks: 'Получена услуга',
    lines: [
      { group: '1', description: 'Месечни мобилни услуги м.07', unit: 'усл.', unitPrice: '84.52', lineTotal: '84.52', lineTax: '16.90', erp: { deal_name: 1, acc_assignment: 1, analytic_parts: [{ acc: '602', level1: '02', level1_name: 'Офис София' }] } },
    ],
  });
  inv.original_filename = 'vivacom_07_2026.pdf';
  inv._processingUntil = Date.now() + 4900;
  return inv;
}
